import {
  AIIcon,
  SyntheticDataGenerationIcon,
  AIKnowledgeManagementSolutionsIcon,
  DefiIcon,
  XRPLIcon,
  StellarIcon,
  BlockchainIcon,
  SmartContractIcon,
  ZeroKnowledgeProofIcon,
  AwsIcon,
  FintechIcon,
  WebIcon,
  MobileIcon,
  RustWebAppIcon,
} from '@rumblefish/ui/Rumblefish23Theme';

export enum TabsValues {
  blockchain,
  ai,
  software,
}

export const ourServicesData = {
  title: 'Our Development Services',
  categories: [
    {
      value: TabsValues.blockchain,
      title: 'Blockchain Development',
      description: `Rumble Fish delivers end-to-end blockchain development services - from smart contracts and DeFi protocols to zero-knowledge proofs and XRPL solutions - backed by battle-tested expertise. Whether you're launching a new Web3 product or scaling an existing protocol, we take full ownership of the technology so you can focus on building your business.`,
      services: [
        {
          name: 'Blockchain Software Development',
          description: (
            <>
              Looking for a blockchain development company that actually gets
              it? Rumble Fish specializes in building robust, production-ready
              decentralized products that people actually want to use - not just
              proof-of-concepts gathering dust. Our blockchain developers have
              worked with industry giants like MakerDAO, helping transform
              complex blockchain concepts into beautiful, functional Web3
              products.
              <br />
              <br />
              Our team combines deep technical expertise in Ethereum, Solidity,
              XRPL, multi-chain EVM development, and decentralized architecture
              with an obsessive focus on user experience. Whether you're
              building a DEX platform, dApp, NFT marketplace, lending protocol,
              or any other Web3 solution, we handle the entire blockchain
              development lifecycle - from smart contract architecture to
              frontend integration and ongoing maintenance.
              <br />
              <br />
              Our blockchain software development approach emphasizes
              battle-tested technologies over experimental frameworks,
              security-first architecture, and seamless integration with
              existing systems. We work with startups launching their first DeFi
              protocol and established enterprises exploring blockchain
              integration. From ideation through deployment and beyond, Rumble
              Fish takes full ownership of your blockchain development project,
              acting as your trusted technology partner and advisor, not just
              another vendor executing tickets.
            </>
          ),
          icon: BlockchainIcon,
          linkTo: '/services/blockchain-software-development/',
        },
        {
          name: 'Zero-Knowledge Proof Development',
          description: (
            <>
              Zero-knowledge proofs are powering the most innovative blockchain
              solutions right now. Rumble Fish stands at the forefront of ZKP
              development, building privacy-preserving systems and scalable
              Layer 2 solutions that actually work in production. With the
              global zero-knowledge proof market projected to reach $7.59
              billion by 2033 and over $28 billion locked in ZK-based rollups,
              this technology has moved from theoretical promise to practical
              necessity.
              <br />
              <br />
              Our zero-knowledge proof development services encompass the full
              spectrum of ZKP applications: privacy-preserving transactions,
              zk-rollup implementations, confidential smart contracts,
              decentralized identity systems, and verifiable computation. We've
              built decentralized registries leveraging zero-knowledge
              cryptography to verify data without exposing sensitive
              information, proving that ZKP isn't just theoretical, it's ready
              for real-world deployment.
              <br />
              <br />
              Whether you need zk-SNARKs for shielded transactions, zk-STARKs
              for transparent scalability solutions, or PLONK-based systems for
              flexible verification, our blockchain researchers and cryptography
              experts design custom ZKP solutions tailored to your specific
              requirements. We work with frameworks like Circom, Noir, gnark,
              and arkworks, implementing R1CS circuits, arithmetic circuits, and
              proof generation workflows that optimize for proof size,
              verification time, and overall system performance.
              <br />
              <br />
              Our zero-knowledge proof development process includes designing zk
              circuits, implementing constraint systems, orchestrating proving
              and verification mechanisms, and integrating cryptographic proof
              systems into modern applications while maintaining privacy and
              data security. From confidential DeFi protocols to
              privacy-preserving identity verification and compliance solutions
              that don't compromise user data, we deliver ZKP systems that meet
              enterprise-grade security standards while maintaining usability.
              <br />
              <br />
              Why choose Rumble Fish for ZKP development? We combine deep
              cryptographic expertise with practical blockchain implementation
              experience. We don't just understand the math, we know how to
              build production systems that leverage zero-knowledge proofs to
              solve real business problems while delivering exceptional user
              experiences.
            </>
          ),
          icon: ZeroKnowledgeProofIcon,
          linkTo: '/services/zero-knowledge-proof-development/',
        },
        {
          name: 'XRPL Blockchain Development',
          description: (
            <>
              The XRP Ledger offers something most blockchains can't match:
              native features for enterprise-grade payments, built-in DEX
              functionality, and settlement in 3-5 seconds with transaction
              costs under $0.0002. Rumble Fish's XRPL blockchain development
              services help fintech companies, payment service providers, and
              DeFi innovators leverage these powerful capabilities to build
              next-generation financial applications.
              <br />
              <br />
              Unlike Ethereum or other smart contract platforms that require
              external protocols for basic functionality, XRPL provides native
              support for tokenization, automated market makers, NFTs, and
              cross-border payments right out of the box. Our XRPL development
              team leverages these built-in features to create production-ready
              applications faster and more cost-effectively than traditional
              blockchain development approaches.
              <br />
              <br />
              Our XRPL blockchain development services include building payment
              applications that settle cross-border transactions more
              efficiently than traditional banking systems, integrating XRP as a
              bridge currency to reduce liquidity requirements, developing DEX
              platforms that combine trading and AMM functionality, creating NFT
              marketplaces leveraging XRPL's native NFT capabilities, and
              building custom tokenization solutions for real-world assets.
              <br />
              <br />
              We work with xrpl.js library, Xumm wallet integration, XRPL
              webhooks and subscriptions, rippled node infrastructure, and the
              complete XRPL development toolchain. Our team understands XRPL's
              unique architecture, from account reserves and trust lines to
              payment paths and multi-signing capabilities, enabling us to build
              applications that fully exploit the network's advantages.
              <br />
              <br />
              Whether you're a financial institution exploring blockchain for
              cross-border settlements, a fintech startup building
              next-generation payment infrastructure, or an enterprise
              integrating XRPL into existing systems, Rumble Fish delivers XRPL
              blockchain solutions that meet enterprise-grade security and
              compliance requirements while maintaining the performance and
              cost-effectiveness that make XRPL compelling.
            </>
          ),
          icon: XRPLIcon,
          linkTo: '/services/xrpl-blockchain-development/',
        },
        {
          name: 'Stellar Product Development',
          description: (
            <>
              Rumble Fish builds production software on Stellar and Soroban for
              fintech startups, enterprises, and financial institutions. Our work
              covers cross-border payment rails, tokenization and stablecoin
              platforms, Soroban smart contracts, and DeFi applications, all
              built around Stellar's fast settlement, very low fees, and native
              financial tooling. We handle the full stack ourselves, from Rust
              smart contracts through APIs and frontends to AWS infrastructure,
              so a project stays with one team from first design through to
              mainnet deployment.
              <br />
              <br />
              Few development partners can say this: the Stellar Development
              Foundation has funded our work on the network twice. Under Stellar
              Community Fund grant #41 we're building a Soroban-first block
              explorer that turns raw on-chain data into readable views, and
              under grant #42 we're building a unified Prices API covering
              real-time and historical data across classic and Soroban assets.
              Both are pieces of core infrastructure that other projects depend
              on, which means we know Stellar and Soroban from working inside
              them, not just from reading the documentation.
              <br />
              <br />
              Every engagement starts with an honest assessment of whether
              Stellar is the right fit, followed by an architecture designed for
              the load you actually expect. We build and audit on testnet, deploy
              to mainnet with monitoring in place, and stay on for support after
              launch. Whether you're planning a payments product, a tokenization
              platform, or a DeFi protocol, we can take it from idea to working
              system. Tell us what you have in mind and we'll help you get it to
              market.
            </>
          ),
          icon: StellarIcon,
          linkTo: '/services/stellar-product-development/',
        },
        {
          name: 'Smart Contract Development',
          description: (
            <>
              Smart contracts are the backbone of every DeFi protocol, NFT
              marketplace, and Web3 application, but they're also where most
              projects fail. One vulnerability can mean millions in losses.
              Rumble Fish's smart contract development services deliver
              auditable, secure, and gas-optimized smart contracts that power
              mission-critical blockchain applications.
              <br />
              <br />
              Our smart contract development expertise spans multiple blockchain
              platforms and programming languages. We specialize in Solidity
              development for Ethereum and EVM-compatible chains, Rust smart
              contract development for Solana and Substrate-based blockchains,
              Vyper for additional security guarantees, and custom smart
              contract languages for specialized networks. Whether you need
              ERC-20 token contracts, ERC-721 and ERC-1155 NFT standards,
              complex DeFi protocols with lending and borrowing logic, DAO
              governance systems, or cross-chain bridges, our smart contract
              developers have built it.
              <br />
              <br />
              What distinguishes Rumble Fish's approach to smart contract
              development? Security is our priority. We implement best practices
              including reentrancy guards, proper access controls, secure random
              number generation, and comprehensive testing coverage. Our code
              goes through rigorous internal audits before deployment, and we
              design smart contracts with upgradeability patterns when
              requirements call for it, whether proxy patterns or our innovative
              non-proxy upgradeability solutions.
              <br />
              <br />
              We don't just write smart contracts, we architect complete smart
              contract ecosystems. Our developers understand how smart contracts
              interact with frontend applications, oracles, Layer 2 solutions,
              and other protocols. We handle gas optimization to keep
              transaction costs manageable, implement efficient storage
              patterns, and design smart contract interfaces that enable
              seamless integration.
              <br />
              <br />
              From MVPs for crypto-native startups to enterprise blockchain
              solutions handling millions in transaction value, Rumble Fish
              delivers smart contract development services backed by deep
              blockchain expertise and battle-tested by projects like MakerDAO.
              We take ownership of the entire process: requirements analysis,
              architecture design, development, testing, deployment, and ongoing
              support. Your smart contracts hold value—trust them to developers
              who understand what's at stake.
            </>
          ),
          icon: SmartContractIcon,
          linkTo: '/services/smart-contract-development/',
        },
        {
          name: 'DeFi Development',
          description: (
            <>
              Rumble Fish's DeFi development services combine extensive
              blockchain knowledge with deep understanding of financial
              mechanisms, enabling us to build cryptocurrency exchanges, lending
              protocols, staking platforms, and innovative DeFi products that
              work seamlessly.
              <br />
              <br />
              We've successfully delivered DeFi solutions for projects at every
              stage, from seed-funded startups building their first DEX to
              established protocols needing advanced features. Our DeFi
              development expertise includes building decentralized exchanges
              (DEXs) with swapping, liquidity pools, automated market makers
              (AMMs), and DAO voting mechanisms, developing lending and
              borrowing protocols with dynamic interest rates and
              collateralization, creating staking platforms and yield farming
              solutions, implementing tokenization platforms for real-world
              assets, and building comprehensive DeFi dashboards and analytics
              tools.
              <br />
              <br />
              What makes Rumble Fish exceptional for DeFi development? We
              understand the financial primitives that make DeFi work. Our team
              grasps concepts like impermanent loss, liquidation mechanisms,
              oracle dependencies, and economic attack vectors. We don't just
              build according to specifications; we challenge assumptions,
              identify potential issues, and architect DeFi solutions that are
              secure, capital-efficient, and user-friendly.
              <br />
              <br />
              Security is paramount in DeFi development where smart contracts
              often hold millions in value. We implement robust security
              protocols including comprehensive testing, internal code reviews,
              and preparation for external audits. Our DeFi platforms balance
              the need for decentralization with practical considerations like
              gas optimization, user experience, and regulatory compliance where
              applicable.
              <br />
              <br />
              Our DeFi development process emphasizes iterative building and
              fast time-to-market. We help you launch an MVP quickly, gather
              user feedback, and iterate based on real-world usage patterns.
              Whether you're building the next generation of decentralized
              exchanges, creating innovative lending protocols, or developing
              novel DeFi primitives, Rumble Fish brings the technical expertise
              and financial understanding necessary to succeed in the
              competitive DeFi landscape.
              <br />
              <br />
              The DeFi space moves fast. Our developers stay current with the
              latest protocols, understand emerging trends like liquid staking
              and real-world asset tokenization, and bring that knowledge to
              every DeFi development project. Partner with a team that's been
              building DeFi since before it was cool.
            </>
          ),
          icon: DefiIcon,
          linkTo: '/services/defi-development/',
        },
      ],
    },
    {
      value: TabsValues.ai,
      title: 'AI Development',
      description: `Our AI development services span custom chat assistants, synthetic data generation for privacy-preserving datasets, and cutting-edge AI research & development exploring novel applications. Whether you need intelligent conversational AI with RAG frameworks, synthetic data to overcome training limitations, or R&D partnerships to push the boundaries of what's possible with AI, we combine academic rigor with production-grade engineering to deliver solutions that actually work.`,
      services: [
        {
          name: 'AI Chat Assistant Development',
          description: (
            <>
              Standard chatbots that just redirect users to blog posts? That's
              so 2020. Modern customers expect AI-powered chat assistants that
              actually understand context, retrieve relevant information, and
              provide personalized responses. Rumble Fish's AI chat assistant
              development services build custom conversational AI solutions
              powered by large language models like GPT, Claude, Llama, and
              Gemini.
              <br />
              <br />
              Our AI chatbot development expertise encompasses RAG-based chatbot
              development (Retrieval-Augmented Generation) that combines LLMs
              with your proprietary knowledge base, custom LLM-powered chatbots
              fine-tuned for specific business requirements, conversational AI
              that understands context and maintains coherent multi-turn
              dialogues, integration with vector databases for semantic search
              capabilities, and enterprise-grade AI assistants that handle
              sensitive information securely.
              <br />
              <br />
              What sets Rumble Fish's AI chat assistant development apart? We
              don't use one-size-fits-all solutions. We analyze your business
              case, evaluate your existing tech stack, and craft the most
              effective AI chatbot development strategy for your specific needs.
              Whether that means implementing RAG with OpenAI's GPT-4, using
              Claude for nuanced reasoning, or deploying open-source models for
              data sovereignty, we choose technologies based on your
              requirements, not vendor preferences.
              <br />
              <br />
              Our AI chatbot development process includes comprehensive
              discovery workshops to understand your use cases, designing
              conversation flows and user experiences, selecting and
              implementing appropriate LLM backends, integrating with vector
              databases like Qdrant or Pinecone for knowledge retrieval,
              developing custom APIs for system integration, implementing
              security measures for data privacy, and ongoing optimization based
              on user interactions.
              <br />
              <br />
              Why choose Rumble Fish for AI chat assistant development? We're
              full-stack developers who build production-grade AI systems. We
              handle the entire technology stack from frontend interfaces to
              backend LLM orchestration, vector database management, and AWS
              infrastructure. Our team delivers AI chatbots that actually
              improve customer satisfaction while reducing support costs.
              <br />
              <br />
              The AI chatbot landscape evolves rapidly. Rumble Fish stays
              current with the latest LLM developments, understands techniques
              like prompt engineering and few-shot learning, and brings that
              expertise to every AI chat assistant development project. If your
              business could benefit from intelligent conversational AI, and it
              probably could, let's talk.
            </>
          ),
          icon: AIIcon,
          linkTo: '/services/ai-chat-assistant/',
        },
        {
          name: 'Synthetic Data Generation',
          description: (
            <>
              When your product needs realistic data for AI training, testing,
              or development but real data is unavailable, restricted by privacy
              regulations, or simply doesn't exist yet, Rumble Fish delivers
              custom synthetic data engineering solutions. Unlike off-the-shelf
              platforms that rely on pre-built generators and configuration, we
              engineer bespoke synthetic data pipelines from the ground up,
              tailored precisely to your unique requirements. Our expertise
              spans multi-modal data generation where text, images, and
              structured data must maintain logical consistency; domain-specific
              intelligence that captures your industry's nuances; and complex
              constraint satisfaction that respects your application's business
              rules.
              <br />
              <br />
              Whether you're generating training datasets for machine learning
              models, creating privacy-safe development environments, building
              procedural content for gaming applications, or simulating future
              scenarios that haven't occurred yet, our team takes full ownership
              of the engineering challenge. From initial discovery through
              deployment and beyond, we handle the complexity so you can focus
              on building your product. When generic synthetic data platforms
              fall short and your requirements demand custom engineering, we've
              got you covered.
              <br />
              <br />
            </>
          ),
          icon: SyntheticDataGenerationIcon,
          linkTo: '/services/synthetic-data-generation/',
        },
        {
          name: 'AI Knowledge Management Solutions',
          description: (
            <>
              We build custom AI knowledge management systems that solve a
              massive problem most enterprises don't even realize they have.
              Your company sits on years of valuable knowledge, like
              documentation, expertise, processes, lessons learned, but it's
              scattered across SharePoint, Confluence, Slack, email, and a dozen
              other systems nobody can navigate efficiently. Employees waste
              about 30% of their day hunting for information, and half of all
              business decisions get made on gut feel simply because finding the
              right data takes too damn long. Generic knowledge management
              platforms promise to fix this, but they fall short when you've got
              complex data architectures, legacy systems, industry-specific
              terminology, and actual security requirements that matter.
              <br />
              <br />
              What makes us different is that we don't sell you a subscription
              to someone else's platform. Our team engineers custom solutions
              using RAG (Retrieval-Augmented Generation) architecture that
              connects large language models directly to your proprietary
              knowledge base. It understands your specific business context,
              terminology, and workflows. When an employee asks a question, the
              AI searches your entire knowledge base, pulls relevant information
              from verified sources, and generates precise answers with
              citations. It's like having an expert who's read every document
              your company has ever created and can instantly recall exactly
              what you need.
              <br />
              <br />
              The results are measurable and significant. Organizations
              implementing these systems see 40-70% reductions in search time,
              which translates to about 45-75 minutes saved per employee daily.
              For a 1,000-person company, that's $5-8 million in annual
              productivity gains with a typical ROI of 300-500% within the first
              year. But beyond the numbers, you get faster onboarding, better
              decision-making with complete information, improved customer
              support response times, and easier compliance management. And
              here's the kicker: you own everything. Complete source code,
              trained models, documentation, and infrastructure.
            </>
          ),
          icon: AIKnowledgeManagementSolutionsIcon,
          linkTo: '/services/ai-knowledge-management-solutions/',
        },
      ],
    },
    {
      value: TabsValues.software,
      title: 'Software Development',
      description: `From AWS cloud infrastructure and fintech platforms to web applications and mobile apps, Rumble Fish crafts state-of-the-art digital products using battle-tested technologies like React, Node.js, Rust, and serverless architectures. Our full-stack expertise and true partnership approach mean we handle everything - design, development, security, deployment, and ongoing support - delivering solutions that exceed expectations and drive real business results.`,
      services: [
        {
          name: 'AWS Cloud Solutions',
          description: (
            <>
              Cloud infrastructure is about building scalable, secure, and
              cost-effective systems that grow with your business. Rumble Fish's
              AWS cloud solutions leverage Amazon Web Services' comprehensive
              toolkit to deliver enterprise-grade infrastructure that's
              purpose-built for blockchain projects, fintech applications, and
              modern software systems.
              <br />
              <br />
              Our AWS development services encompass the full spectrum of cloud
              architecture: designing and implementing serverless architectures
              using AWS Lambda, API Gateway, and DynamoDB; building
              containerized applications with ECS, EKS, and Fargate;
              implementing Infrastructure as Code (IaC) using CloudFormation,
              CDK, and Terraform; setting up CI/CD pipelines with CodeBuild,
              CodePipeline, and GitLab CI; and architecting secure, compliant
              environments meeting financial services regulations.
              <br />
              <br />
              What makes Rumble Fish exceptional for AWS cloud solutions? We
              don't just deploy to the cloud - we architect cloud-native
              systems. Our development teams use the serverless framework
              extensively, enabling granular scaling and cost optimization. We
              implement the Infrastructure as Code principle, ensuring high
              maintainability, version control for infrastructure, and
              reproducible deployments across environments.
              <br />
              <br />
              For blockchain projects, AWS provides the preferred
              infrastructure. We've built fully managed ledger databases
              offering centralized, immutable, and cryptographically verifiable
              transaction logs. We integrate blockchain nodes with AWS
              infrastructure, implement secure key management using AWS KMS, and
              design architectures that balance decentralization with practical
              operational requirements.
              <br />
              <br />
              Our AWS cloud solutions emphasize security and compliance from the
              ground up. We implement enterprise-grade security measures,
              including VPC isolation, security groups and NACLs, encryption at
              rest and in transit, comprehensive logging and monitoring with
              CloudWatch and CloudTrail, and automated backup and disaster
              recovery strategies. For fintech and healthcare applications, we
              ensure compliance with relevant regulations while maintaining
              performance and cost-effectiveness.
              <br />
              <br />
              Whether you're a startup needing scalable infrastructure for rapid
              growth, an established company migrating legacy systems to the
              cloud, or a blockchain project requiring specialized cloud
              architecture, Rumble Fish delivers AWS solutions tailored to your
              needs. We take ownership of the entire process: requirements
              analysis, architecture design, implementation, optimization, and
              ongoing support.
              <br />
              <br />
              The cloud can be a real competitive advantage when implemented
              correctly. Our AWS expertise helps you leverage cloud services for
              enhanced data security at lower costs, easy scalability and
              management, reduced operational overhead, and faster
              time-to-market. From the plethora of AWS services available, we
              help you choose and implement the ones that serve your business
              the best way possible.
            </>
          ),
          icon: AwsIcon,
          linkTo: '/services/aws-cloud-solutions/',
        },
        {
          name: 'Fintech Software Development',
          description: (
            <>
              Traditional financial institutions are struggling to compete with
              nimble fintechs and big tech companies. Rumble Fish's fintech
              software development services help both challengers and incumbents
              build innovative financial technology systems that compete at the
              highest level.
              <br />
              <br />
              Our fintech development expertise spans the entire financial
              services ecosystem. We build payment solutions, including mobile
              wallets for contactless payments, payment gateways for e-commerce
              platforms, and peer-to-peer payment platforms enabling direct
              transfers without intermediaries. We develop comprehensive banking
              software encompassing core banking systems, digital banking
              platforms, mobile banking apps, loan origination and processing
              systems, and fraud detection and risk management tools.
              <br />
              <br />
              For investment and trading, we create robust platforms for retail
              and institutional investors, including robo-advisors for automated
              investment management, portfolio management systems, algorithmic
              trading solutions, and high-frequency trading infrastructure. We
              also specialize in RegTech solutions that automate compliance,
              monitoring, and reporting requirements, helping financial
              institutions navigate complex regulatory landscapes efficiently.
              <br />
              <br />
              What distinguishes Rumble Fish's fintech software development? We
              combine financial services expertise with cutting-edge technology.
              Our team leverages advanced technologies, including artificial
              intelligence for predictive analytics and fraud detection, machine
              learning for credit scoring and risk assessment, blockchain for
              transparent and secure transactions, and cloud computing for
              scalable infrastructure. We understand both the technology and the
              financial mechanisms that make fintech work.
              <br />
              <br />
              Our fintech development process emphasizes rapid iteration and
              market validation. We help you launch MVPs quickly, test with real
              users, and iterate based on feedback. Whether you're building a
              challenger bank, creating innovative payment infrastructure,
              developing investment platforms, or modernizing legacy financial
              systems, Rumble Fish brings the technical expertise and financial
              services understanding necessary to succeed.
              <br />
              <br />
              The fintech landscape evolves rapidly. Our team stays current with
              emerging technologies, understands regulatory developments, and
              brings that knowledge to every fintech software development
              project. We've worked on solutions for online trading,
              cryptocurrency exchanges, digital wallets, and marketplaces,
              giving us deep domain knowledge. Partner with developers who
              understand both code and capital.
            </>
          ),
          icon: FintechIcon,
          linkTo: '/services/fintech-software-development/',
        },
        {
          name: 'Web development',
          description: (
            <>
              Your website isn't just a digital business card; it's often the
              first (and last) impression potential customers get. Rumble Fish's
              web development services deliver beautiful, technologically
              advanced web applications and corporate websites that stand out
              from the competition and actually drive business results.
              <br />
              <br />
              Our web development expertise encompasses full-stack development
              using battle-tested technologies. On the frontend, we build
              responsive, intuitive interfaces with React, Redux, Material UI,
              and modern CSS frameworks. Our backend development leverages
              Node.js, NestJS, Express, and Kafka for robust server-side
              applications. We implement serverless architectures using AWS
              Lambda and API Gateway, containerized deployments with Docker and
              Kubernetes, and comprehensive API development (REST, GraphQL).
              <br />
              <br />
              We offer specialized web development services, including custom
              web applications tailored to your specific business requirements,
              customer web portals providing unified platforms for product
              information and self-service, corporate website development with
              sleek design and fast performance, e-commerce platforms with
              secure payment integration, and progressive web apps (PWAs)
              delivering app-like experiences through browsers.
              <br />
              <br />
              What makes Rumble Fish exceptional for web development? We combine
              aesthetic excellence with technical sophistication. Our web
              applications aren't just pretty; they're performant, secure,
              scalable, and maintainable. We follow Infrastructure as Code
              principles, ensuring high maintainability, implement comprehensive
              testing and QA processes, optimize for speed and SEO, and design
              systems that scale with your business growth.
              <br />
              <br />
              For web portals and complex applications, we handle advanced
              requirements including integration with CRM platforms like HubSpot
              and Salesforce, implementation of robust authentication and
              authorization, real-time features using WebSockets, advanced
              search and filtering capabilities, comprehensive analytics and
              reporting dashboards, and responsive design ensuring flawless
              experiences across devices.
              <br />
              <br />
              Whether you're a startup needing an MVP web application, an
              established company requiring a sophisticated web portal, or an
              enterprise modernizing legacy systems, Rumble Fish delivers web
              development solutions that exceed expectations. We take full
              ownership of your web development project, acting as your true
              technology partner - not just another development shop executing
              specifications.
              <br />
              <br />
              The web moves fast. Our developers stay current with the latest
              frameworks, understand modern web architecture patterns, and bring
              that expertise to every web development project. Your digital
              presence matters. Make it count with web development from Rumble
              Fish.
            </>
          ),
          icon: WebIcon,
          linkTo: '/services/web-development/',
        },
        {
          name: 'Mobile development',
          description: (
            <>
              In a mobile-first world, your app needs to be exceptional. Rumble
              Fish's mobile development services deliver fast, secure, and
              beautiful iOS and Android applications that users actually love.
              Whether you need a native app for a specific platform or
              cross-platform development reaching both ecosystems, we craft
              mobile experiences with that elusive "wow factor."
              <br />
              <br />
              Our mobile development services encompass iOS native app
              development optimized for performance, security, and user
              experience using Swift and UIKit; Android native app development
              that's visually appealing, highly functional, and compatible
              across device ranges using Kotlin and Jetpack; cross-platform app
              development with React Native delivering native look and feel
              across platforms with a single codebase; and mobile UI/UX design
              services creating intuitive, engaging interfaces that enhance
              usability.
              <br />
              <br />
              For cross-platform mobile development, we leverage React Native to
              maximize reach while minimizing development time and costs. This
              approach delivers consistent user experiences across iOS and
              Android while maintaining high performance and native features. We
              implement platform-specific optimizations when needed, ensuring
              your app performs flawlessly regardless of the underlying
              technology.
              <br />
              <br />
              Our mobile development capabilities include implementing complex
              features like real-time messaging and notifications, integration
              with device hardware (camera, GPS, biometrics), secure
              authentication and authorization, offline functionality with smart
              syncing, integration with backend APIs and services, payment
              processing and in-app purchases, and comprehensive mobile
              analytics and crash reporting.
              <br />
              <br />
              Security is paramount in mobile development, especially for
              fintech and healthcare applications. We implement secure data
              storage, encrypted communications, biometric authentication,
              certificate pinning, and comprehensive security testing to protect
              user data and prevent unauthorized access.
              <br />
              <br />
              Whether you're building a fintech app handling sensitive financial
              data, a healthcare application requiring HIPAA compliance, an
              e-commerce platform with seamless checkout, or any other mobile
              product, Rumble Fish delivers mobile development services that
              balance beauty with functionality, security with usability, and
              native performance with development efficiency.
              <br />
              <br />
              The mobile landscape evolves constantly. Our developers stay
              current with platform updates, understand emerging mobile
              technologies, and bring that expertise to your mobile development
              project. Your customers are mobile. Make sure your app meets their
              expectations with mobile development from Rumble Fish.
            </>
          ),
          icon: MobileIcon,
          linkTo: '/services/mobile-development/',
        },
        {
          name: 'Rust Web App Development',
          description: (
            <>
              Rust is rapidly becoming the language of choice for
              performance-critical applications, WebAssembly development, and
              systems where security isn't negotiable. Rumble Fish's Rust web
              app development services leverage Rust's memory safety,
              concurrency guarantees, and blazing performance to build web
              applications that are fast, secure, and maintainable.
              <br />
              <br />
              Why choose Rust for web development? Rust compiles directly to
              machine code, offering C/C++-level performance. Its ownership
              model prevents entire classes of bugs at compile time—no null
              pointer exceptions, no data races, no memory leaks. For web
              applications handling sensitive data, processing high-volume
              transactions, or requiring predictable performance, Rust delivers
              advantages that higher-level languages simply can't match.
              <br />
              <br />
              Our Rust web development expertise includes building
              high-performance web APIs and microservices, developing
              WebAssembly modules for client-side computation, creating backend
              services for blockchain applications, implementing data processing
              pipelines, and building secure, concurrent web servers using
              frameworks like Actix-web, Rocket, and Axum.
              <br />
              <br />
              What makes Rumble Fish exceptional for Rust web app development?
              We've been using Rust since before it was trendy. Our team
              leverages Rust for smart contract development (Solana, Substrate),
              backend services requiring high performance, and applications
              where security is critical. We understand Rust's ownership model,
              lifetime annotations, and async/await patterns - not just the
              syntax, but the underlying concepts that make Rust powerful.
              <br />
              <br />
              For Web3 projects, Rust is often the preferred choice. We build
              Rust-based smart contracts for Solana and Substrate chains,
              integrate Rust backends with blockchain nodes, develop
              high-performance indexers and analytics services, and create
              WebAssembly modules for client-side cryptographic operations.
              <br />
              <br />
              The Rust ecosystem offers robust tooling: cargo for package
              management, a comprehensive standard library, and frameworks like
              Tokio for async I/O. We leverage this ecosystem to build
              production-grade Rust web applications that are maintainable,
              testable, and scalable.
              <br />
              <br />
              Admittedly, Rust has a learning curve as concepts like ownership,
              lifetimes, and borrowing take time to master. But the payoff is
              enormous: applications that are fast, secure, and correct by
              construction. Our Rust expertise means you don't have to climb
              that learning curve - we bring battle-tested Rust development
              knowledge to your project from day one.
              <br />
              <br />
              Whether you're building a high-performance API service, creating
              WebAssembly modules for browser-based computation, developing
              blockchain infrastructure, or implementing systems where security
              and performance are non-negotiable, Rumble Fish delivers Rust web
              app development services that leverage Rust's unique strengths.
              The future of performance-critical web development is increasingly
              Rust-shaped. Our team stays current with Rust ecosystem
              developments, understands emerging patterns, and brings that
              expertise to every Rust web app development project. When
              performance, security, and correctness all matter, Rust is the
              answer, and Rumble Fish knows how to make it work.
            </>
          ),
          icon: RustWebAppIcon,
          linkTo: '/services/rust-web-app-development/',
        },
      ],
    },
  ],
};
